<script lang="ts" setup>
import { useBalance } from '@wagmi/vue';
import { storeToRefs } from 'pinia';

import { useCartStore } from '@/stores/cart/useCartStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { ScopeWagmiKeys } from '@/types';
import { useNuxtApp } from '#app';
import { Web3AccountInfoStatus } from '~/components/shared/web3/Web3AccountInfo/Web3AccountInfo.types';

const cartStore = useCartStore();

const userStore = useUserStore();

defineProps<{
    isPaymentLoading?: boolean;
}>();

const {
    cart,
    cartValues,
    totalSumm
} = storeToRefs(cartStore);

const { '$wagmi': { account } } = useNuxtApp();

const { address, chain, chainId, isConnected } = account;

const { data: balance } = useBalance({
    chainId,
    address: userStore.user || '0x',
    scopeKey: ScopeWagmiKeys.USE_BALANCE,
    query: { enabled: !!userStore.user }
});

const emit = defineEmits([
    'onPay',
    'toCart'
]);

</script>

<template>
  <div class="cart-payment">
    <Transition>
      <div v-if="chain"
           class="height-full display-flex flex-column"
      >
        <h5 class="mb-4 compact-500-accent">
          Checkout
        </h5>
        <p class="cart-payment__amount">
          {{ cart.size }} item
        </p>
        <div>
          <div class="cart-payment-list">
            <CartItem v-for="({ displayImageURL, price, name, itemType, uuid, amount, maxAmount, isError }, index) in cartValues.active"
                      :key="uuid"
                      :amount="amount"
                      :class="index !== cart.size - 1 && 'network-payment-list__item'"
                      :displayImageURL="displayImageURL"
                      :is-error="isError"
                      :itemType="itemType"
                      :max-amount="maxAmount"
                      :name="name"
                      :price="`${price?.amount} ${price?.symbol}`"
                      :uuid="uuid"
                      :without-close="true"
                      class="display-flex width-full"
            />
          </div>

          <Suspense>
            <template #fallback>
              <div class="display-flex align-items-center gap-16 cart-payment__account">
                <LoaderSkeleton :width="52"
                                :height="52" />
                <LoaderSkeleton class="grow"
                                :height="52" />
              </div>
            </template>
            <LazyWeb3AccountInfo :address="address!"
                                 :chain-name="chain!.name"
                                 :balance="balance! "
                                 :status="isConnected ? Web3AccountInfoStatus.CONNECTED : undefined"
                                 class="cart-payment__account"
            />
          </Suspense>
        </div>
        <div class="cart-payment-footer">
          <CartTotal v-bind="totalSumm" />
          <div class="display-flex align-items-center">
            <Button :size="'size.small'"
                    class="align-self-stretch"
            >
              <button aria-label="to cart"
                      type="button"
                      @click="emit('toCart')"
              >
                <IconMain :size="24"
                          icon="arrow-l-r"
                />
              </button>
            </Button>
            <Button :color="'bg.mojito'"
                    :is-loading="isPaymentLoading"
                    :is-disabled="!balance || !isConnected"
                    :size="'size.medium'"
                    class="grow ml-8"
            >
              <button class="display-flex align-items-center justify-center compact-500-accent gap-8"
                      type="button"
                      @click="emit('onPay')"
              >
                Proceed to payment
                <IconMain :size="24"
                          icon="arrow-r-l"
                />
              </button>
            </Button>
          </div>
        </div>
      </div>

      <div v-else>
        <span>Please check the network connection</span>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.cart-payment {
  height: 100%;

  &__amount {
    margin-bottom: $spacing-400;
  }

  &__account {
    margin-top: $spacing-500;
  }

  &-list {
    padding-bottom: $spacing-500;
    border-bottom: 1px solid var(--border-content);

    &__item {
      margin-bottom: $spacing-400;
    }
  }

  &-footer {
    margin-top: auto;
  }
}

</style>
